import { DIETARY_LABELS, type AIConstraints, type DietaryLabel } from '@recipes/contracts';
import { Checkbox, Input, Label } from '@recipes/ui';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '@/features/shared/form-field';
import { dietaryLabel } from '@/features/shared/labels';

export type ConstraintsState = {
  servings: string;
  maxTotalMinutes: string;
  cuisine: string;
  variants: 1 | 2 | 3;
  dietaryPreferences: DietaryLabel[];
  equipment: string;
};

export const EMPTY_CONSTRAINTS: ConstraintsState = {
  servings: '',
  maxTotalMinutes: '',
  cuisine: '',
  variants: 1,
  dietaryPreferences: [],
  equipment: '',
};

export function toAiConstraints(state: ConstraintsState, language: 'de' | 'en'): AIConstraints {
  const num = (v: string) => (v.trim() === '' ? null : Number(v));
  return {
    servings: num(state.servings),
    maxTotalMinutes: num(state.maxTotalMinutes),
    cuisine: state.cuisine.trim() || null,
    variants: state.variants,
    dietaryPreferences: state.dietaryPreferences,
    equipment: state.equipment
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 20),
    language,
  };
}

export function ConstraintsFields({
  value,
  onChange,
  showVariants = true,
}: {
  value: ConstraintsState;
  onChange: (next: ConstraintsState) => void;
  showVariants?: boolean;
}) {
  const { t } = useTranslation();
  const groupId = useId();
  const set = <K extends keyof ConstraintsState>(key: K, v: ConstraintsState[K]) =>
    onChange({ ...value, [key]: v });
  return (
    <fieldset className="space-y-4 rounded-lg border p-3">
      <legend className="px-1 text-sm font-semibold">{t('ai.constraints.title')}</legend>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <FormField label={t('ai.constraints.servings')}>
          <Input
            type="number"
            min={1}
            max={100}
            inputMode="numeric"
            value={value.servings}
            onChange={(e) => set('servings', e.target.value)}
          />
        </FormField>
        <FormField label={t('ai.constraints.maxMinutes')}>
          <Input
            type="number"
            min={1}
            inputMode="numeric"
            value={value.maxTotalMinutes}
            onChange={(e) => set('maxTotalMinutes', e.target.value)}
          />
        </FormField>
        <FormField label={t('ai.constraints.cuisine')}>
          <Input value={value.cuisine} onChange={(e) => set('cuisine', e.target.value)} />
        </FormField>
        {showVariants && (
          <FormField label={t('ai.constraints.variants')} hint={t('ai.constraints.variantsHint')}>
            <Input
              type="number"
              min={1}
              max={3}
              inputMode="numeric"
              value={value.variants}
              onChange={(e) =>
                set('variants', Math.min(3, Math.max(1, Number(e.target.value) || 1)) as 1 | 2 | 3)
              }
            />
          </FormField>
        )}
      </div>
      <FormField label={t('ai.constraints.equipment')} hint={t('ai.constraints.equipmentHint')}>
        <Input value={value.equipment} onChange={(e) => set('equipment', e.target.value)} />
      </FormField>
      <div>
        <Label id={`${groupId}-dietary`}>{t('ai.constraints.dietary')}</Label>
        <p className="text-xs text-muted-foreground">{t('ai.constraints.dietaryHint')}</p>
        <div
          role="group"
          aria-labelledby={`${groupId}-dietary`}
          className="mt-2 flex flex-wrap gap-2"
        >
          {DIETARY_LABELS.map((label) => {
            const checked = value.dietaryPreferences.includes(label);
            return (
              <label
                key={label}
                className="flex min-h-[36px] items-center gap-2 rounded-full border px-3 text-sm has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-accent"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(c) =>
                    set(
                      'dietaryPreferences',
                      c === true
                        ? [...value.dietaryPreferences, label]
                        : value.dietaryPreferences.filter((l) => l !== label),
                    )
                  }
                />
                {dietaryLabel(t, label)}
              </label>
            );
          })}
        </div>
      </div>
    </fieldset>
  );
}
